// StatusBezuege.tsx — die Rückfrage „für wen?" zu den gewählten Statusrollen.
//
// Manche Rollen sind ohne Gegenüber keine Aussage: „Verwalter" allein sagt
// nichts, „Verwalter — für wen?" schon. Welche Rolle ein Gegenüber verlangt,
// steht im Katalog (`data/statusAuswahlKatalog.ts`, Feld `bezug`); hier wird
// nur gefragt.
//
// Erfasst wird mit demselben Bauteil wie überall sonst in der Anlage
// (`ContactRefField`): bestehenden Kontakt SUCHEN oder neu EINTIPPEN, ohne
// jeden Schreibzugriff. Bei einer Firma steht zusätzlich „Daten befüllen" neben
// der Internet-Adresse — wer hier eine Firma anlegt, hat meist nur Name und
// Webseite.
//
// Gespeichert wird NICHTS hier: Die Bezüge leben im Form-State und werden erst
// beim Speichern zu Beziehungen (siehe relationsInline/persistRelations.ts).

import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { ContactRefField } from "../../relationsInline/ContactRefField";
import type { ContactRef } from "../../relationsInline/types";
import {
  bezugsArtVon,
  bezugsFrage,
  haushaltsAngabeVon,
  type BezugsArt,
  type StatusEintrag,
} from "../../../data/statusAuswahlKatalog";
import { RELATIONSHIP_CATALOG, RELATIONSHIP_FREE } from "../../../utils/householdRole";
import { FieldRow, LabeledSelect, LabeledInput } from "../formPrimitives";
import { GegenueberKarte, GegenueberRahmen } from "./GegenueberKasten";

const useLocal = makeStyles({
  hinweis: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.78rem",
    color: REFINED.ink2,
    lineHeight: 1.4,
  },
});

/**
 * Ein erfasster Bezug: die Rolle und der Kontakt, für den sie gilt.
 *
 * Bei den Beziehungsrollen kommt die Verwandtschaft dazu. Sie ist dort kein
 * Beiwerk, sondern der gespeicherte Beziehungstyp selbst — ohne sie verwirft
 * `persistRelations` die Zeile ersatzlos.
 */
export type StatusBezug = {
  rolle: string;
  ref: ContactRef | null;
  /** Aus RELATIONSHIP_CATALOG, oder RELATIONSHIP_FREE für den Freitext. */
  verwandtschaft?: string;
  /** Freitext: was die andere Person für diesen Kontakt ist. */
  freitext?: string;
  /** Freitext: was dieser Kontakt für die andere Person ist. */
  freitextRueck?: string;
};

/** Liest die Bezüge aus dem Formularfeld — unlesbares gilt als leer. */
export const bezuegeAusFeld = (roh: unknown): StatusBezug[] => {
  if (typeof roh !== "string" || !roh.trim()) return [];
  try {
    const p = JSON.parse(roh);
    return Array.isArray(p) ? (p as StatusBezug[]) : [];
  } catch {
    return [];
  }
};

/** Schreibt die Bezüge ins Formularfeld. */
export const bezuegeInsFeld = (liste: StatusBezug[]): string =>
  liste.length ? JSON.stringify(liste) : "";

/**
 * Welcher Kontakttyp im Auswahlfeld vorgeschlagen wird.
 *
 * `"kontakt"` heißt: beides ist möglich. Vorgeschlagen wird dann die Firma —
 * ein Verwalter, ein Betreiber oder ein Leasinggeber ist in aller Regel eine —
 * und der Umschalter im Eintipp-Modus bleibt offen.
 */
const typVon = (art: BezugsArt): "Person" | "Firma" =>
  art === "person" ? "Person" : "Firma";

/** Die angehakten Rollen, die ein Gegenüber verlangen — in ihrer Reihenfolge. */
const rollenMitBezug = (eintraege: StatusEintrag[], rollen: string[]) =>
  rollen
    .map((r) => ({ rolle: r, art: bezugsArtVon(eintraege, r) }))
    .filter((x): x is { rolle: string; art: BezugsArt } => !!x.art);

/**
 * Was an dieser Rolle noch fehlt. Bei den Beziehungsrollen zählt eine leere
 * Freitext-Zeile als offen — sonst ginge sie beim Speichern verloren, ohne
 * dass es jemand bemerkt.
 */
const bezugUnvollstaendig = (
  eintraege: StatusEintrag[],
  bezuege: StatusBezug[],
  rolle: string,
): boolean => {
  const b = bezuege.find((x) => x.rolle === rolle);
  if (!b?.ref) return true;
  if (!haushaltsAngabeVon(eintraege, rolle)) return false;
  if (b.verwandtschaft === RELATIONSHIP_FREE) return !(b.freitext ?? "").trim();
  return !b.verwandtschaft;
};

/**
 * Wie viele Kästen `StatusBezuege` zeigt und wie viele davon offen sind — für
 * den gemeinsamen Rahmen im Status-Schritt (`GegenueberRahmen`).
 */
export const bezugsStand = (
  eintraege: StatusEintrag[],
  rollen: string[],
  bezuege: StatusBezug[],
): { anzahl: number; offen: number } => {
  const zuFragen = rollenMitBezug(eintraege, rollen);
  return {
    anzahl: zuFragen.length,
    offen: zuFragen.filter((x) => bezugUnvollstaendig(eintraege, bezuege, x.rolle)).length,
  };
};

export function StatusBezuege({
  eintraege,
  rollen,
  bezuege,
  onChange,
  idBase = "statusbezug",
  ohneRahmen = false,
}: {
  /** Der Katalog, aus dem die Rollen stammen. */
  eintraege: StatusEintrag[];
  /** Die aktuell angehakten Rollen, in ihrer Reihenfolge. */
  rollen: string[];
  bezuege: StatusBezug[];
  onChange: (liste: StatusBezug[]) => void;
  idBase?: string;
  /**
   * Nur die Kästen — Einleitung und Hinweis setzt der Aufrufer, weil er
   * weitere Kästen daneben stellt (Status-Schritt mit Folgefeldern).
   */
  ohneRahmen?: boolean;
}) {
  const l = useLocal();

  // Nur die Rollen, die ein Gegenüber verlangen — in der Reihenfolge, in der
  // sie angehakt wurden.
  const zuFragen = rollenMitBezug(eintraege, rollen);

  if (zuFragen.length === 0) return null;

  const bezugVon = (rolle: string): StatusBezug | undefined =>
    bezuege.find((b) => b.rolle === rolle);

  const refVon = (rolle: string): ContactRef | null => bezugVon(rolle)?.ref ?? null;

  const setzeRef = (rolle: string, ref: ContactRef | null) => {
    const ohne = bezuege.filter((b) => b.rolle !== rolle);
    // Ein geleertes Feld entfernt den Eintrag ganz, statt `null` zu speichern:
    // Ein Bezug ohne Kontakt ist kein Bezug, und beim Speichern stünde sonst
    // eine Beziehung ohne Gegenüber in der Liste.
    if (!ref) return onChange(ohne);
    const alt = bezugVon(rolle);
    // Die Verwandtschaft überlebt einen Wechsel des Kontakts: Wer die Person
    // korrigiert, hat deshalb nicht die Beziehung geändert.
    onChange([
      ...ohne,
      {
        ...alt,
        rolle,
        ref,
        verwandtschaft:
          alt?.verwandtschaft ??
          (haushaltsAngabeVon(eintraege, rolle) ? RELATIONSHIP_CATALOG[0] : undefined),
      },
    ]);
  };

  const setzeFeld = (rolle: string, patch: Partial<StatusBezug>) => {
    const alt = bezugVon(rolle);
    if (!alt) return;
    onChange(bezuege.map((b) => (b.rolle === rolle ? { ...b, ...patch } : b)));
  };

  const { offen } = bezugsStand(eintraege, rollen, bezuege);

  const kaesten = zuFragen.map(({ rolle, art }) => {
    const haus = haushaltsAngabeVon(eintraege, rolle);
    const b = bezugVon(rolle);
    const frei = b?.verwandtschaft === RELATIONSHIP_FREE;
    return (
      <GegenueberKarte key={rolle} frage={bezugsFrage(rolle, art)}>
        <ContactRefField
          idBase={`${idBase}-${rolle.replace(/\s+/g, "-").toLowerCase()}`}
          wantType={typVon(art)}
          value={refVon(rolle)}
          onChange={(r) => setzeRef(rolle, r)}
          // „kontakt" lässt beides zu — der Umschalter bleibt offen.
          allowTypeSwitch={art === "kontakt"}
          mitBefuellen
        />
        {/* Die Verwandtschaft nur bei den Beziehungsrollen — und erst,
            wenn eine Person gewählt ist: Vorher wüsste man nicht, wessen
            Tochter gemeint ist. */}
        {haus && b?.ref && (
          <>
            <FieldRow>
              <LabeledSelect
                label="Was ist die Person für diesen Kontakt?"
                name={`${idBase}-verw-${rolle.replace(/\s+/g, "-").toLowerCase()}`}
                value={b.verwandtschaft ?? ""}
                onChange={(v) => setzeFeld(rolle, { verwandtschaft: v })}
                options={[
                  ...RELATIONSHIP_CATALOG.map((r) => ({ value: r, label: r })),
                  { value: RELATIONSHIP_FREE, label: "Sonstige (Freitext) …" },
                ]}
              />
            </FieldRow>
            {frei && (
              <FieldRow>
                <LabeledInput
                  label="… ist für diesen Kontakt"
                  name={`${idBase}-frei-${rolle.replace(/\s+/g, "-").toLowerCase()}`}
                  value={b.freitext ?? ""}
                  onChange={(v) => setzeFeld(rolle, { freitext: v })}
                  placeholder="Freund, Nachbarin, Geschäftspartner …"
                />
                <LabeledInput
                  label="… und umgekehrt (optional)"
                  name={`${idBase}-freirueck-${rolle.replace(/\s+/g, "-").toLowerCase()}`}
                  value={b.freitextRueck ?? ""}
                  onChange={(v) => setzeFeld(rolle, { freitextRueck: v })}
                  placeholder="Freund, Nachbar, Geschäftspartnerin …"
                />
              </FieldRow>
            )}
          </>
        )}
        {haus && (
          <span className={l.hinweis}>
            {haus.imHaushalt
              ? "Die Person zieht in den Haushalt ein und erbt die Wohnanschrift."
              : "Die Person wird als Beziehung außerhalb des Haushaltes geführt."}
          </span>
        )}
      </GegenueberKarte>
    );
  });

  if (ohneRahmen) return <>{kaesten}</>;
  return (
    <GegenueberRahmen anzahl={zuFragen.length} offen={offen}>
      {kaesten}
    </GegenueberRahmen>
  );
}

export default StatusBezuege;
